import { ScaleLinear, scaleLinear } from 'd3';
import { Feature, FeatureCollection, GeoJsonProperties, Geometry } from 'geojson';
import L from 'leaflet';
import { handleRankingsBySort } from 'src/app/components/CountriesInformationComponent/countries-infomations.config';
import { GeneralMapViewProps } from 'src/app/components/LeafletMapComponent/leaflet.config';
import { EnterpriseRelationsResultType } from 'src/types/enterprise.type';
import { LocationsColumnDataType } from 'src/types/table.type';

/*
  Enterprise identify and location tabs are using this class to display the map
*/
export class CountriesController {
  static instance: CountriesController | null = null;

  countryFeatures: Record<string, Feature<Geometry, GeoJsonProperties>> = {};

  countriesFeatureCollection: FeatureCollection | undefined;

  countriesGeoJSON: L.GeoJSON<Geometry, any> | null = null;

  addedCountries: Record<string, EnterpriseRelationsResultType['from']['rows'] | LocationsColumnDataType> | null = null;

  isMapAlreadyAdded: boolean = false;

  isDashboard: boolean = false;

  /*
    We only want one instance of MapMarkerController during the process, we dont want many of them working on the
    background independetly.
  */
  static getInstance(): CountriesController {
    if (this.instance === null) {
      this.instance = new CountriesController();
    }
    this.instance.resetCountriesControllers();
    return this.instance;
  }

  mapColors: [string, string] = ['', ''];

  countryColor: string = '';

  borderColor: string = '';

  // This field is used to give color to each country depending on their rankings
  countryRankings: Record<string, any> = {};

  areColorsChanged: boolean = false;

  setCountryColor: (color: string) => void = (color) => {
    this.countryColor = color;
  };

  setBorderColor: (color: string) => void = (color) => {
    this.borderColor = color;
  };

  setIsDashboard: (bool: boolean) => void = (bool) => {
    this.isDashboard = bool;
  };

  // A reset function that runs on component unmount.
  resetCountriesControllers() {
    this.countriesGeoJSON?.eachLayer((layer) => {
      layer.removeEventListener('mousemove');
      layer.removeEventListener('mouseout');
      layer.unbindTooltip();
    });
    this.countriesGeoJSON?.remove();
    this.countryFeatures = {};
    this.countriesGeoJSON = null;
    this.addedCountries = null;
    this.countriesFeatureCollection = undefined;
    this.isMapAlreadyAdded = false;
    this.mapColors = ['', ''];
    this.countryColor = '';
    this.areColorsChanged = false;
    this.countryRankings = {};
  }

  addLayersForCountryPages = async ({
    map,
    generalMapViewProps,
    countriesFeatureCollection,
  }: {
    map: L.Map;
    generalMapViewProps: GeneralMapViewProps;
    countriesFeatureCollection: FeatureCollection;
  }) => {
    try {
      // We need to understand if the colors are not changed and if the map is already initialized
      // if so wo need to return without doing anything here.
      this.checkIsColorsChanged(generalMapViewProps.mapColor);
      if (this.isMapAlreadyAdded && !this.areColorsChanged && this.addedCountries) return;
      const { addedCountries, addedSupplierCountries, sortBy } = generalMapViewProps;
      if ((addedCountries?.length || addedSupplierCountries?.length) && !this.addedCountries) {
        // If addedCountries or supplierCountries comes from function call, and we do not have any added countries we need to
        // update our this.addedCountries from the obj.
        const countriesObj: Record<string, any> = {};
        if (addedSupplierCountries?.length) {
          addedSupplierCountries.forEach((country) => {
            const alphaCodeCountry: string = country.fromInterest.enterpriseIdentified.country.alphaCode;
            if (country.isMonitored) {
              countriesObj[alphaCodeCountry] = [...(countriesObj[alphaCodeCountry] || []), country];
            }
          });
        } else if (addedCountries?.length) {
          addedCountries.forEach((country) => {
            const alphaCodeCountry: string = country.alphaCode;
            countriesObj[alphaCodeCountry] = country as LocationsColumnDataType;
          });
        }
        this.addedCountries = countriesObj;
      }
      let newCountryRankings: Record<string, number> = {};
      if (addedCountries && !sortBy) {
        addedCountries.forEach((eachCountry: LocationsColumnDataType) => {
          newCountryRankings = {
            ...newCountryRankings,

            [eachCountry.alphaCode]: addedCountries.length - (eachCountry?.prevalenceIndexRank || 0),
          };
        });
        this.countryRankings = newCountryRankings;
      } else if (addedCountries && sortBy) {
        const sortedCountryRankings = handleRankingsBySort(sortBy, addedCountries);
        if (sortedCountryRankings) {
          this.countryRankings = sortedCountryRankings;
        }
      }

      // in the end we call addCountriesGeoJsonToTheMap function.
      if (!this.countriesFeatureCollection) {
        this.countriesFeatureCollection = countriesFeatureCollection;
        this.addCountriesGeoJsonToTheMap({
          generalMapViewProps,
          map,
        });
      } else {
        this.addCountriesGeoJsonToTheMap({
          generalMapViewProps,
          map,
        });
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      return null;
    }
  };

  addCountriesGeoJsonToTheMap({ generalMapViewProps, map }: { generalMapViewProps: GeneralMapViewProps; map: L.Map }) {
    const { median, smallestNumber } = this.medianAndSmallestNumberGenerator(this.countryRankings);

    if (this.countriesGeoJSON) {
      this.countriesGeoJSON.remove();
    }

    const { actionOnClick, mapColor } = generalMapViewProps;

    const scale = scaleLinear([1, 10], mapColor);

    const localAddedCountries = this.addedCountries;
    const { countryRankings, isDashboard } = this;

    // The function below gives each country a click event, and adds styling to that specific country
    const geoJSON = L.geoJSON(this.countriesFeatureCollection, {
      onEachFeature(feature: Feature<Geometry, any>, layer) {
        if (feature?.properties?.name) {
          layer.on('mouseover', () => {
            layer.bindTooltip(feature.properties.name, {
              permanent: true,
              sticky: true,
              interactive: false,
              direction: 'top',
            });
            layer.openTooltip();
          });
          layer.on('mouseout', () => {
            layer.closeTooltip();
            layer.unbindTooltip();
          });
        }
        if (actionOnClick) {
          layer.on('click', (e: L.LeafletMouseEvent) => {
            if (isDashboard) {
              const url = '/Interests?tab=4&subTab=2';
              window.location.href = url;
            } else if (localAddedCountries) {
              const selectedCountry = localAddedCountries[feature.properties.alphaCode];
              if (selectedCountry) {
                geoJSON.resetStyle();
                e.target.setStyle({ fillColor: '#EDE1CB' });
                actionOnClick(selectedCountry);
              }
            }
          });
        }
      },
      style: (feature) => {
        if (feature) {
          const countryId = feature.properties.alphaCode;
          return {
            fillColor:
              localAddedCountries && localAddedCountries[countryId]
                ? this.countryColorGenerator(countryRankings[countryId], smallestNumber, median, scale)
                : this.countryColor
                  ? this.countryColor
                  : '#C2C2C2',
            color: this.borderColor ? this.borderColor : '#414a4c',
            weight: 0.5,
            opacity: 1,
            fillOpacity: 1,
          };
        }
        return {};
      },
    });
    // Finaly we add this countries with new events and stylings to the map.
    this.countriesGeoJSON = geoJSON;
    this.countriesGeoJSON.addTo(map);
    this.isMapAlreadyAdded = true;
  }

  // This function helps us the generate colors for each country
  countryColorGenerator = (
    countryRanking: number,
    smallestNumber: number,
    median: number,
    scale: ScaleLinear<string, string, never>,
  ) => {
    if (countryRanking < smallestNumber + median) {
      return scale(1);
    }
    if (countryRanking < smallestNumber + median * 2) {
      return scale(2);
    }
    if (countryRanking < smallestNumber + median * 3) {
      return scale(3);
    }
    if (countryRanking < smallestNumber + median * 4) {
      return scale(4);
    }
    if (countryRanking < smallestNumber + median * 5) {
      return scale(5);
    }
    if (countryRanking < smallestNumber + median * 6) {
      return scale(6);
    }
    if (countryRanking < smallestNumber + median * 7) {
      return scale(7);
    }
    if (countryRanking < smallestNumber + median * 8) {
      return scale(8);
    }
    if (countryRanking < smallestNumber + median * 9) {
      return scale(9);
    }
    return scale(10);
  };

  checkIsColorsChanged(newColors: [string, string]) {
    const checkerArr: string[] = [];
    this.mapColors.forEach((color) => {
      if (newColors.includes(color)) {
        checkerArr.push(color);
      }
    });
    if (checkerArr.length === 2) {
      this.areColorsChanged = false;
    } else {
      this.areColorsChanged = true;
    }
    this.mapColors = newColors;
  }

  medianAndSmallestNumberGenerator = (rankings: Record<string, number>) => {
    const countryRankings = Object.values(rankings);
    const smallestNumber = Math.min(...countryRankings);
    const biggestNumber = Math.max(...countryRankings);
    const median = (Math.abs(smallestNumber) + biggestNumber) / 10;
    return { median, smallestNumber };
  };
}
